import { Component, input, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators, FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AccountService } from '../../../../Utils/_services/account/account.service';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { PasswordModule } from 'primeng/password';
import { Button } from "primeng/button";
import { ConfirmService } from '../../../../Utils/_services/confirmation-dialog.service';
import { AdminSecurityService } from '../../../../Utils/_services/admin/security/security.service';
import { ActiveSessionsComponent, ISessionRow } from './active-sessions/active-sessions.component';
import { TwoFactorAuthComponent } from './two-factor-auth/two-factor-auth.component';
import { IActiveSession } from '../../../../Utils/_services/admin/security/security.model';

function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPass = group.get('newPassword')?.value;
  const confirm = group.get('confirmPassword')?.value;
  if (newPass && confirm && newPass !== confirm) {
    group.get('confirmPassword')?.setErrors({ mismatch: true });
    return { mismatch: true };
  }
  if (group.get('confirmPassword')?.hasError('mismatch')) {
    group.get('confirmPassword')?.setErrors(null);
  }
  return null;
}
@Component({
  selector: 'app-account-setting',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    TagModule,
    PasswordModule,
    Button,
    FormsModule,
    ActiveSessionsComponent,
    TwoFactorAuthComponent
  ],
  providers: [MessageService],
  templateUrl: './account-setting.component.html',
  styleUrl: './account-setting.component.css',
})
export class AccountSettingComponent {
  accountForm: FormGroup;
  saving = false;
  email = input<string>('');
  
  selected2FA: string = 'email';
  activeSessions: ISessionRow[] = [
    { deviceId: 'FP-8A9C1D', device: 'MacBook Pro 16"', deviceType: 'Laptop', location: 'Dallas, TX', ip: '192.168.1.1', lastActive: new Date(), current: true },
    { deviceId: 'RN-12B34C', device: 'iPhone 13', deviceType: 'Phone', location: 'Austin, TX', ip: '10.0.0.5', lastActive: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), current: false },
    { deviceId: 'FP-5D6E7F', device: 'Windows Desktop', deviceType: 'Desktop', location: 'Houston, TX', ip: '172.16.0.12', lastActive: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), current: false }
  ];
show2FA = signal(false);
showSessions = signal(false);


  constructor(private fb: FormBuilder,
    private msgSvc: MessageService,
    private accountSvc: AccountService,
    private confirmationService : ConfirmService,
    private adminSecuritySvc: AdminSecurityService
  ) {
    this.accountForm = this.fb.group({
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],  // ← empty string here
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.minLength(8)]],
      confirmPassword: ['']
    }, { validators: passwordMatchValidator });
  }

  ngOnInit(): void {
    this.accountForm.patchValue({ email: this.email() }); 
    this.loadSecuritySettings();
  }

  loadSecuritySettings(): void {
    this.adminSecuritySvc.getSecuritySettings().subscribe({
      next: (res) => {
        if (res.data) {
          // Set selected 2FA
          this.selected2FA = res.data.twoFactorAuth?.method || 'email';
          
          const currentDeviceId = localStorage.getItem('device_id');
          // Set Active Sessions
          this.activeSessions = res.data.activeSessions?.map((s: IActiveSession): ISessionRow => ({
            sessionId: s.sessionId,
            deviceId: s.deviceId,
            deviceType: s.deviceType,
            device: s.deviceName,
            ip: s.ipAddress,
            location: s.location,
            lastActive: s.lastActive,
            createdAt: s.createdAt,
            current: s.deviceId === currentDeviceId
          })) || [];
        }
      },
      error: (err) => {
        console.error('Failed to load security settings', err);
      }
    });
  }

  async on2FAChange(method: 'email' | 'sms' | 'voice'): Promise<void> {

    if (this.selected2FA === method) return;

    const confirmed = await this.confirmationService.confirm({
      bodyHeader: 'Change 2FA Method',
      bodySubtitle: `Are you sure you want to change your Two-Factor Authentication method to ${method}?`,
      yesButtonText: 'Change',
      noButtonText: 'Cancel',
    });

    if (confirmed) {
      this.adminSecuritySvc.update2FASettings({ enabled: true, method }).subscribe({
        next: () => {
          this.selected2FA = method; // Update UI only on success
          this.msgSvc.add({ severity: 'success', summary: 'Updated', detail: `2FA method set to ${method}` });
        },
        error: (err) => {
          this.msgSvc.add({ severity: 'error', summary: 'Error', detail: 'Failed to update 2FA settings' });
        }
      });
    }
  }


  isInvalid(field: string): boolean {
    const ctrl = this.accountForm.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  getError(field: string): string {
    const ctrl = this.accountForm.get(field);
    if (!ctrl || !ctrl.errors) return '';
    if (ctrl.errors['required']) return 'This field is required.';
    if (ctrl.errors['email']) return 'Enter a valid email address.';
    if (ctrl.errors['minlength']) return `Minimum ${ctrl.errors['minlength'].requiredLength} characters required.`;
    if (ctrl.errors['mismatch']) return 'Passwords do not match.';
    return 'Invalid value.';
  }

  saveChanges(): void {
    this.accountForm.markAllAsTouched();
    if (this.accountForm.invalid) return;

    const { email, currentPassword, newPassword, confirmPassword } = this.accountForm.getRawValue();

    if (!newPassword) {
      this.msgSvc.add({ severity: 'info', summary: 'No changes', detail: 'Enter a new password to update.' });
      return;
    }
    this.accountSvc.changePassword({ email, currentPassword, newPassword, confirmPassword })
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.msgSvc.add({ severity: 'success', summary: 'Success', detail: res.msg });
          this.accountForm.reset();
        },
        error: (err) => {
          this.saving = false;
          const msg = err?.error?.message || err?.error?.Errors?.message || 'Failed to change password.';
          this.msgSvc.add({ severity: 'error', summary: 'Error', detail: msg });
        }
      });
  }

  get formReady(): boolean {
    const { email, currentPassword, newPassword, confirmPassword } = this.accountForm.getRawValue(); // ← here too
    return (
      !!email &&
      !!currentPassword &&
      !!newPassword &&
      !!confirmPassword &&
      newPassword === confirmPassword &&
      newPassword.length >= 8 &&
      this.accountForm.valid
    );
  }

  cancelChanges(): void {
    const email = this.accountForm.get('email')?.value;
    this.accountForm.reset();
    this.accountForm.patchValue({
      email: email
    });

    this.msgSvc.add({
      severity: 'info',
      summary: 'Cancelled',
      detail: 'Changes discarded.'
    });
  }

  addRole(): void {
    this.msgSvc.add({ severity: 'info', summary: 'Add Role', detail: 'Role creation coming soon.' });
  }



  manageTwoFa(): void {
    this.msgSvc.add({ severity: 'info', summary: 'Two-Factor Authentication', detail: '2FA management coming soon.' });
  }

  manageSessions(): void {
    this.msgSvc.add({ severity: 'info', summary: 'Active Sessions', detail: 'Session management coming soon.' });
  }
  toggle2FA(): void {
  this.show2FA.update(v => !v);
}

toggleSessions(): void {
  this.showSessions.update(v => !v);
}

async confirmRevoke(session: any): Promise<void> {
  const confirmed = await this.confirmationService.confirm({
    bodyHeader: 'Revoke Session',
    bodySubtitle: 'Are you sure you want to revoke this active session? The user will be logged out immediately.',
    yesButtonText: 'Revoke',
    noButtonText: 'Cancel',
  });

  if (confirmed) {
    this.adminSecuritySvc.revokeSession(session.sessionId).subscribe({
      next: () => {
        this.msgSvc.add({ severity: 'success', summary: 'Success', detail: 'Session revoked successfully.' });
        this.loadSecuritySettings(); // Refresh the list
      },
      error: () => {
        this.msgSvc.add({ severity: 'error', summary: 'Error', detail: 'Failed to revoke session' });
      }
    });
  }
}
}

